import { createSelector } from '@ngrx/store';
import { BookingContingentReservation, BookingTariff } from '@products/models/booking.model';
import { getAllBookedTariffs, getBookedTariff } from '@store/products/booking/product/product-booking.selectors';

/**
 * Gets all booked tariffs with workshops
 * @description Get all booked tariffs which have booked at least one workshop
 * @returns Tariffs which have booked at least one workshop
 */
export const getAllBookedWorkshopTariffs = createSelector(
  getAllBookedTariffs,
  (bookedTariffs: BookingTariff[]) => bookedTariffs.filter((bookedTariff) => bookedTariff.workshops.length) || []
);

/**
 * Get all booked contingent tariffs
 * @description Get all booked contingent tariffs with contingent reservation
 * @return Booked tariff or empty array
 */
export const getAllBookedContingentTariffs = createSelector(getAllBookedTariffs, (bookedTariffs: BookingTariff[]) => {
  const contingentBookedTariffs: BookingTariff[] = [];

  bookedTariffs.forEach((bookedTariff) => {
    if (bookedTariff.contingentReservations.length) {
      contingentBookedTariffs.push(bookedTariff);
    }
  });

  return contingentBookedTariffs;
});

/**
 * Get all booked contingent reservations
 * @description Get all booked contingent reservations
 * @return Contingent reservation or empty array
 */
export const getAllBookedContingentReservations = createSelector(
  getAllBookedContingentTariffs,
  (bookedTariffs: BookingTariff[]) => {
    const bookedContingentReservations: BookingContingentReservation[] = [];

    bookedTariffs.forEach((bookedTariff) =>
      bookedTariff.contingentReservations.forEach((bookedContingentReservation) =>
        bookedContingentReservations.push(bookedContingentReservation)
      )
    );

    return bookedContingentReservations;
  }
);

/**
 * Are all booked contingent reservations valid
 * @description Check if all booked contingent reservations are valid
 * @returns Boolean
 */
export const areBookedContingentReservationsValid = createSelector(
  getAllBookedContingentReservations,
  (bookedContingentReservations: BookingContingentReservation[]) =>
    bookedContingentReservations.every((bookedContingentReservation) => bookedContingentReservation.isValid)
);

/**
 * Is contingent reservation from date picked and invalid
 * @description Check if contingent reservation from date is picked and invalid
 * @return Boolean
 */
export const isBookedContingentReservationFromDateSetAndInvalid = createSelector(
  getAllBookedContingentReservations,
  (bookedContingentReservations: BookingContingentReservation[]) =>
    bookedContingentReservations.some(
      (bookedContingentReservation) => bookedContingentReservation.fromDate && !bookedContingentReservation.isValid
    )
);

/**
 * Get booked tariff contingent reservations
 * @description Get booked tariff contingent reservations array
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Contingent reservation or empty array
 */
export const getBookedTariffContingentReservations = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getBookedTariff(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    (bookedTariff: BookingTariff): BookingContingentReservation[] =>
      bookedTariff ? bookedTariff.contingentReservations : []
  );

/**
 * Are booked tariff contingent reservations valid
 * @description Check if booked tariff contingent reservations are valid
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Boolean
 */
export const areBookedTariffContingentReservationsValid = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getBookedTariffContingentReservations(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    (bookedContingentReservations: BookingContingentReservation[]) =>
      bookedContingentReservations.every((bookedContingentReservation) => bookedContingentReservation.isValid)
  );

/**
 * Get booked tariff contingent reservations with selected date
 * @description Get booked tariff contingent reservations array with selected date
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Contingent reservation or empty array
 */
export const getBookedTariffContingentReservationsWithSelectedDate = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getBookedTariffContingentReservations(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    (bookedContingentReservations: BookingContingentReservation[]): BookingContingentReservation[] =>
      bookedContingentReservations.filter((bookedContingentReservation) => bookedContingentReservation.fromDate)
  );
